import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Grid, Card, CardContent, Button,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, IconButton, Tooltip, TextField, Tab, Tabs
} from '@mui/material';
import {
  Timer as TimerIcon,
  AttachMoney as MoneyIcon,
  Schedule as ScheduleIcon,
  Download as DownloadIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import ModernLayout from '../common/ModernLayout';
import { StatsCard } from '../common/ModernCard';
import { GradientButton } from '../common/ModernButton';
import axiosInstance from '../axiosConfig';

const TimeTrackerDashboard = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [timeEntries, setTimeEntries] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    start_date: new Date(new Date().setDate(1)).toISOString().split('T')[0], // First day of month
    end_date: new Date().toISOString().split('T')[0] // Today
  });

  useEffect(() => {
    fetchTimeEntries();
    fetchSummary();
  }, [filters]);

  const fetchTimeEntries = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get('/api/time-tracker/entries', {
        params: {
          start_date: filters.start_date,
          end_date: filters.end_date,
          status: 'stopped'
        }
      });
      setTimeEntries(response.data.data || []);
    } catch (error) {
      console.error('Error fetching time entries:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSummary = async () => {
    try {
      const response = await axiosInstance.get('/api/time-tracker/reports/summary', {
        params: filters
      });
      setSummary(response.data.data);
    } catch (error) {
      console.error('Error fetching summary:', error);
    }
  };

  const formatDuration = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours}h ${minutes}m`;
  };

  const exportToCSV = () => {
    const headers = ['Fecha', 'Proyecto', 'Tarea', 'Duración', 'Facturable', 'Costo', 'Descripción'];
    const rows = timeEntries.map(entry => [
      new Date(entry.started_at).toLocaleDateString(),
      entry.project_name || '-',
      entry.task_title || '-',
      formatDuration(entry.duration),
      entry.is_billable ? 'Sí' : 'No',
      entry.total_cost ? `$${entry.total_cost.toFixed(2)}` : '-',
      entry.description || '-'
    ]);

    const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `time_entries_${filters.start_date}_to_${filters.end_date}.csv`;
    link.click();
  };

  return (
    <ModernLayout title="Time Tracker">
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 3 }}>
          Registro de Tiempo
        </Typography>

        {/* Summary Cards */}
        {summary && (
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <StatsCard
                title="Total Horas"
                value={summary.total_hours}
                subtitle="Horas registradas"
                icon={<TimerIcon />}
                color="primary"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatsCard
                title="Horas Facturables"
                value={summary.billable_hours}
                subtitle="Tiempo facturable"
                icon={<ScheduleIcon />}
                color="success"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatsCard
                title="Ingresos"
                value={`$${summary.total_revenue.toFixed(2)}`}
                subtitle="Total generado"
                icon={<MoneyIcon />}
                color="warning"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatsCard
                title="Días Trabajados"
                value={summary.days_worked}
                subtitle="En este período"
                icon={<ScheduleIcon />}
                color="info"
              />
            </Grid>
          </Grid>
        )}

        {/* Filters */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
              <TextField
                type="date"
                label="Desde"
                value={filters.start_date}
                onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
                InputLabelProps={{ shrink: true }}
                size="small"
              />
              <TextField
                type="date"
                label="Hasta"
                value={filters.end_date}
                onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
                InputLabelProps={{ shrink: true }}
                size="small"
              />
              <GradientButton
                startIcon={<DownloadIcon />}
                onClick={exportToCSV}
                disabled={timeEntries.length === 0}
              >
                Exportar CSV
              </GradientButton>
            </Box>
          </CardContent>
        </Card>

        {/* Time Entries Table */}
        <Card>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Registros de Tiempo
            </Typography>

            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Fecha</TableCell>
                    <TableCell>Proyecto</TableCell>
                    <TableCell>Tarea</TableCell>
                    <TableCell>Inicio</TableCell>
                    <TableCell>Fin</TableCell>
                    <TableCell>Duración</TableCell>
                    <TableCell>Facturable</TableCell>
                    <TableCell align="right">Costo</TableCell>
                    <TableCell>Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {timeEntries.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} align="center">
                        <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                          No hay registros de tiempo en este período
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    timeEntries.map((entry) => (
                      <TableRow key={entry.id} hover>
                        <TableCell>
                          {new Date(entry.started_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          {entry.project_name || '-'}
                        </TableCell>
                        <TableCell>
                          {entry.task_title || '-'}
                        </TableCell>
                        <TableCell>
                          {new Date(entry.started_at).toLocaleTimeString()}
                        </TableCell>
                        <TableCell>
                          {entry.ended_at ? new Date(entry.ended_at).toLocaleTimeString() : '-'}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={formatDuration(entry.duration)}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={entry.is_billable ? 'Sí' : 'No'}
                            size="small"
                            color={entry.is_billable ? 'success' : 'default'}
                          />
                        </TableCell>
                        <TableCell align="right">
                          {entry.total_cost ? `$${entry.total_cost.toFixed(2)}` : '-'}
                        </TableCell>
                        <TableCell>
                          <Tooltip title="Ver detalles">
                            <IconButton size="small">
                              <ViewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Box>
    </ModernLayout>
  );
};

export default TimeTrackerDashboard;

